import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { FileCheck, Shield, DollarSign, FileWarning, Users, GraduationCap, ArrowRight } from 'lucide-react';

const Services = () => {
  return (
    <div className="services-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Comprehensive Trade<br />Compliance Solutions
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8">
              From strategic consulting to managed operations, we provide end-to-end support for companies navigating international trade requirements across all global jurisdictions.
            </p>
            <Button size="lg" className="bg-slate-700 hover:bg-slate-800 text-white px-10 h-14">
              Schedule Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FileCheck,
                title: 'Product Classification',
                description: 'Accurate HTS, HS, ECCN, and USML classification across all global jurisdictions',
                link: '/services/product-classification'
              },
              {
                icon: Shield,
                title: 'Export Controls & Licensing',
                description: 'ITAR and EAR compliance, license applications, and jurisdiction determinations',
                link: '/services/export-controls'
              },
              {
                icon: DollarSign,
                title: 'Foreign Trade Zones',
                description: 'FTZ applications, activation, and ongoing operational support for cost savings',
                link: '/services/foreign-trade-zones'
              },
              {
                icon: FileWarning,
                title: 'Voluntary Disclosures',
                description: 'Strategic guidance for self-reporting violations to BIS, DDTC, and CBP',
                link: '/services/voluntary-disclosures'
              },
              {
                icon: Users,
                title: 'Trade Management',
                description: 'Comprehensive managed support without added headcount',
                link: '/services/trade-management'
              },
              {
                icon: GraduationCap,
                title: 'Training & Audits',
                description: 'Export compliance training and comprehensive program assessments',
                link: '/services/training-audits'
              },
            ].map((service, i) => {
              const Icon = service.icon;
              return (
                <a key={i} href={service.link} className="bg-white border border-gray-200 rounded-lg p-8 hover:border-slate-400 hover:shadow-lg transition-all group block">
                  <div className="w-14 h-14 bg-slate-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-slate-100 transition-colors">
                    <Icon className="h-7 w-7 text-slate-700" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <span className="text-slate-700 hover:text-slate-900 font-semibold text-sm flex items-center group">
                    Learn More
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Not Sure Which Service You Need?
          </h2>
          <p className="text-base sm:text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Our team can help assess your specific requirements and recommend the right approach for your business.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Speak with an Expert
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
